/**
 * Writing a post.
 *
 * Photos go through the same upload queue the gallery uses — straight to
 * storage, never through the API — so a post with five photos costs the server
 * a few kilobytes of JSON. The post itself is only created once the transfers
 * have finished, because a post referencing files that do not exist yet would
 * render as blanks in everyone else's feed.
 *
 * The album choice matters more than it looks: a photo attached here is a real
 * photo in the group, and if it is not filed anywhere it lands in "All photos"
 * and nowhere else. Asking up front is cheaper than tidying up later.
 */

import { useEffect, useMemo, useState } from "react";
import { useT } from "../../lib/i18n.js";
import { Button } from "../../components/ui/Button.js";
import { processingPollDelay, whilePageVisible } from "../../lib/pageActivity.js";
import { trpcClient } from "../../lib/trpc.js";
import {
  useUploadQueue,
  type ServerFileStatus,
} from "../upload/useUploadQueue.js";
import type { Album } from "../album/types.js";
import { MAX_POST_PHOTOS } from "./types.js";
import { openFilePicker } from "../../lib/filePicker.js";
import "./WallComposer.css";

export interface WallComposerProps {
  groupId: string;
  albums: Album[];
  canUpload: boolean;
  isPosting: boolean;
  onPost: (input: {
    body: string;
    fileIds: string[];
    folderId: string | null;
  }) => void;
}

export function WallComposer({
  groupId,
  albums,
  canUpload,
  isPosting,
  onPost,
}: WallComposerProps) {
  const t = useT();
  const [body, setBody] = useState("");
  const [folderId, setFolderId] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, string>>({});

  const uploads = useUploadQueue({ groupId, folderId });

  const attached = useMemo(
    () =>
      uploads.tasks.filter(
        (task) => task.status !== "cancelled" && task.status !== "failed",
      ),
    [uploads.tasks],
  );

  /**
   * Ready to be posted, which is not the same as uploaded.
   *
   * A file id appears the moment the bytes are stored, and the server is
   * still decoding, orienting and building thumbnails after that. Posting
   * on the id alone published photographs that had no thumbnail yet, so the
   * wall showed empty frames that filled in later — or did not, if the
   * decode failed, leaving a post permanently pointing at nothing.
   *
   * "duplicate" counts: those bytes were already here and already processed.
   */
  const settled = attached.filter(
    (task) =>
      task.fileId !== undefined &&
      (task.status === "done" || task.status === "duplicate"),
  );
  const busy = attached.length > 0 && settled.length < attached.length;
  /* Told apart so the wait can be explained rather than just imposed. */
  const processing = attached.some((task) => task.status === "processing");
  const canSubmit =
    (body.trim().length > 0 || settled.length > 0) && !busy && !isPosting;

  const addPhotos = (files: File[]) => {
    const room = MAX_POST_PHOTOS - attached.length;
    const accepted = files.slice(0, Math.max(0, room));
    if (accepted.length === 0) return;

    // Local previews, so a thumbnail appears while the bytes are still moving
    // rather than after the server has finished with them.
    setPreviews((current) => {
      const next = { ...current };
      for (const file of accepted)
        next[`${file.name}:${String(file.size)}`] = URL.createObjectURL(file);
      return next;
    });
    uploads.enqueue(accepted);
  };

  /*
   * Watch anything the server is still working on.
   *
   * Without this the composer would wait for a "processing" task to finish and
   * never hear that it had: the queue only learns a server-side status when
   * somebody hands it one, and the gallery — the one screen that does — is not
   * open. Most photographs never get here, because a sealed one is complete the
   * moment it lands; it is the ones the browser could not seal, and which the
   * server therefore has to decode, that take this path.
   *
   * Polling stops the moment nothing is pending, so an idle composer costs
   * nothing at all.
   */
  const pendingIds = attached
    .filter((task) => task.status === "processing" && task.fileId !== undefined)
    .map((task) => task.fileId!);
  const pendingKey = pendingIds.join(",");
  const { applyServerStatuses } = uploads;

  useEffect(() => {
    if (pendingIds.length === 0) return;

    let stopped = false;
    let running = false;
    let nextPollAt = 0;
    const startedAt = Date.now();
    const tick = async () => {
      if (stopped || document.hidden || running || Date.now() < nextPollAt) return;
      running = true;
      try {
        const statuses = new Map<string, ServerFileStatus>();
        await Promise.all(
          pendingIds.map(async (fileId) => {
            try {
              const result = await trpcClient.media.get.query({
                groupId,
                fileId,
              });
              statuses.set(fileId, result.file.status as ServerFileStatus);
            } catch {
              // A file that cannot be read right now is simply asked again.
            }
          }),
        );
        if (!stopped && statuses.size > 0) applyServerStatuses(statuses);
      } finally {
        running = false;
        nextPollAt = Date.now() + processingPollDelay(Date.now() - startedAt);
      }
    };

    const stopPolling = whilePageVisible(() => {
      nextPollAt = 0;
      void tick();
      const timer = setInterval(() => void tick(), 2000);
      return () => clearInterval(timer);
    });
    return () => {
      stopped = true;
      stopPolling();
    };
    // Keyed on the joined id list rather than the array, which is rebuilt on
    // every render and would restart the interval each time.
  }, [pendingKey, groupId, applyServerStatuses]);
  const pick = () => {
    openFilePicker({
      accept: "image/jpeg,image/png,image/webp,image/heic,image/avif",
      multiple: true,
      onPick: addPhotos,
    });
  };

  const submit = () => {
    onPost({
      body: body.trim(),
      fileIds: settled.map((task) => task.fileId!).filter(Boolean),
      folderId,
    });
    setBody("");
    setPreviews({});
    uploads.clearFinished();
  };

  return (
    <section className="fc-composer" aria-label={t("Write a post")}>
      <textarea
        className="fc-composer__input"
        value={body}
        rows={2}
        maxLength={5000}
        placeholder={t("Share something with the group…")}
        onChange={(event) => setBody(event.target.value)}
      />

      {attached.length > 0 && (
        <ul className="fc-composer__thumbs">
          {attached.map((task) => {
            const key = `${task.file.name}:${String(task.file.size)}`;
            const done = task.status === "done" || task.status === "duplicate";
            const working = task.status === "processing";
            return (
              <li
                key={task.id}
                className="fc-composer__thumb"
                data-pending={!done || undefined}
              >
                {previews[key] && (
                  <img src={previews[key]} alt={task.file.name} />
                )}
                {/*
                  Two different waits, said differently. A percentage that
                  reaches 100 and then sits there reads as stuck; the second
                  half of the wait is the server working, and saying so is
                  the difference between waiting and wondering.
                */}
                {!done && (
                  <span className="fc-composer__thumb-progress">
                    {working
                      ? "Processing…"
                      : `${String(Math.round(task.progress * 100))}%`}
                  </span>
                )}
                <button
                  type="button"
                  className="fc-composer__thumb-remove"
                  aria-label={`Remove ${task.file.name}`}
                  onClick={() => uploads.cancel(task.id)}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="fc-composer__row">
        {canUpload && (
          <button
            type="button"
            className="fc-composer__attach"
            onClick={pick}
            disabled={attached.length >= MAX_POST_PHOTOS}
            title={
              attached.length >= MAX_POST_PHOTOS
                ? `${String(MAX_POST_PHOTOS)} photos is the limit`
                : "Add photos"
            }
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="8.5" cy="10" r="1.5" />
              <path d="M21 16l-5-5-4.5 4.5L9 13l-6 6" />
            </svg>
            <span>
              {attached.length > 0
                ? `${String(attached.length)}/${String(MAX_POST_PHOTOS)}`
                : "Photos"}
            </span>
          </button>
        )}

        {/* Only worth asking once there is something to file. */}
        {attached.length > 0 && albums.length > 0 && (
          <label className="fc-composer__album">
            <span className="fc-sr-only">
              {t("Add these photos to an album")}
            </span>
            <select
              value={folderId ?? ""}
              onChange={(event) => setFolderId(event.target.value || null)}
            >
              <option value="">{t("No album")}</option>
              {albums.map((album) => (
                <option key={album.id} value={album.id}>
                  {album.emoji ? `${album.emoji} ` : ""}
                  {album.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <Button
          variant="primary"
          size="sm"
          className="fc-composer__post"
          loading={isPosting || busy}
          disabled={!canSubmit}
          title={
            processing
              ? "Waiting for the photos to finish processing"
              : undefined
          }
          onClick={submit}
        >
          {busy ? "Uploading…" : "Post"}
        </Button>
      </div>
    </section>
  );
}
